import { useMemo, useRef, useState } from 'react';
import { Alert, Button, Toast } from '@openedx/paragon';
import { RiAddLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useDeleteTicket, useFetchUserTickets } from '../../gql';
import ActiveTicketsRow from './components/ActiveTicketsRow';
import DeleteTicketModal from './components/DeleteTicketModal';
import RaiseTicketModal from './components/RaiseTicketModal';
import TicketsTable from './components/TicketsTable';
import messages from './messages';
import './Support.scss';

const Support = () => {
  const intl = useIntl();

  const {
    tickets, loading, error, refetch,
  } = useFetchUserTickets();
  const { deleteTicket, loading: deleting } = useDeleteTicket();

  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [toasts, setToasts] = useState([]);
  const nextToastIdRef = useRef(0);

  const showToast = (message, variant) => {
    if (!message) { return; }
    const id = nextToastIdRef.current;
    nextToastIdRef.current += 1;
    setToasts((prev) => [...prev, { id, message, variant }]);
  };

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const sortedTickets = useMemo(
    () => [...tickets].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
    [tickets],
  );

  const closeDeleteModal = () => setPendingDelete(null);

  const confirmDelete = () => {
    if (!pendingDelete) { return; }
    deleteTicket({
      variables: { keyId: pendingDelete.id },
      onCompleted: () => {
        refetch();
        closeDeleteModal();
        showToast(intl.formatMessage(messages.ticketDeletedSuccess), 'success');
      },
      onError: () => {
        showToast(intl.formatMessage(messages.somethingWentWrong), 'error');
      },
    });
  };

  return (
    <div className="learner-support">
      <header className="learner-support__header">
        <h1 className="learner-support__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <Button
          variant="primary"
          size="lg"
          iconBefore={RiAddLine}
          onClick={() => setModalOpen(true)}
        >
          {intl.formatMessage(messages.raiseTicket)}
        </Button>
      </header>

      {error && (
        <Alert variant="danger" className="learner-support__alert">
          {intl.formatMessage(messages.ticketLoadError)}
        </Alert>
      )}

      <ActiveTicketsRow tickets={sortedTickets} />

      <section className="learner-support__history">
        <h2 className="learner-support__section-title">
          {intl.formatMessage(messages.ticketHistoryTitle)}
        </h2>
        <TicketsTable
          tickets={sortedTickets}
          loading={loading}
          onDelete={setPendingDelete}
        />
      </section>

      <RaiseTicketModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={refetch}
        showToast={showToast}
      />

      <DeleteTicketModal
        isOpen={!!pendingDelete}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        loading={deleting}
      />

      {toasts.map((t) => (
        <Toast
          key={t.id}
          show
          variant={t.variant}
          onClose={() => dismissToast(t.id)}
        >
          {t.message}
        </Toast>
      ))}
    </div>
  );
};

export default Support;
